import { useCallback, useMemo } from "react";

import type { Speaker, Track } from "../../event-experience/schemas";
import { useAdminSpeakers, useAdminTracks } from "../use-admin-content";
import {
  useAttendeeRecords,
  type AttendeeRecord,
} from "../use-attendee-records";
import { isOnRoster, resolveChampions } from "../track-champion";

import { trackSpec } from "./content-specs";
import {
  EntityCrudSection,
  type EntitySpec,
  type LookupOption,
  type LookupSource,
} from "./EntityCrudSection";

/**
 * Tracks: the shared CRUD form, plus the attendees and speakers the champion
 * field picks from (TNG-95).
 *
 * Its own wrapper, like SpeakersSection, because the champion picker reads
 * the whole roster and the speaker list, which the other content tabs have no
 * reason to pay for.
 *
 * A track names up to two champions, each picked from the same list. The
 * picker of one disables the person already chosen in the other.
 *
 * A speaker who is the same person as a roster attendee is offered once, as
 * the attendee: that link is the one whose profile the attendee app opens.
 */

type Slot = "champion" | "secondChampion";
const KEYS: Record<Slot, { attendee: string; speaker: string }> = {
  champion: { attendee: "championAttendeeId", speaker: "championSpeakerId" },
  secondChampion: {
    attendee: "secondChampionAttendeeId",
    speaker: "secondChampionSpeakerId",
  },
};

/** Who a picker value names, whichever slot it is in: `attendee:id`. */
function personOf(value: unknown): string | null {
  const match = /^(?:second)?[cC]hampion(Attendee|Speaker)Id:(.+)$/.exec(
    String(value ?? ""),
  );
  return match ? `${match[1]!.toLowerCase()}:${match[2]}` : null;
}
export function TracksSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const tracks = useAdminTracks(eventId);
  const attendees = useAttendeeRecords(eventId);
  const speakers = useAdminSpeakers(eventId);

  const attendeeList = useMemo(
    () => (attendees.status === "success" ? attendees.data : []),
    [attendees],
  );
  const speakerList = useMemo(
    () => (speakers.status === "success" ? speakers.data : []),
    [speakers],
  );
  const attendeesById = useMemo(
    () => new Map<string, AttendeeRecord>(attendeeList.map((a) => [a.id, a])),
    [attendeeList],
  );
  const speakersById = useMemo(
    () => new Map<string, Speaker>(speakerList.map((s) => [s.id, s])),
    [speakerList],
  );

  const optionsFor = useMemo(
    () =>
      (slot: Slot): LookupOption[] => {
        const onRoster = attendeeList.filter(isOnRoster);
        const rosterIds = new Set(onRoster.map((attendee) => attendee.id));
        return [
          ...onRoster.map((attendee) => ({
            id: `${KEYS[slot].attendee}:${attendee.id}`,
            label: attendee.name,
            detail: [
              "Attendee",
              [attendee.role, attendee.company].filter(Boolean).join(", "),
            ]
              .filter(Boolean)
              .join(" · "),
          })),
          ...speakerList
            .filter(
              (speaker) =>
                !speaker.attendeeId || !rosterIds.has(speaker.attendeeId),
            )
            .map((speaker) => ({
              id: `${KEYS[slot].speaker}:${speaker.id}`,
              label: speaker.name,
              detail: [
                "Speaker",
                [speaker.role, speaker.company].filter(Boolean).join(", "),
              ]
                .filter(Boolean)
                .join(" · "),
            })),
        ].sort((a, b) => a.label.localeCompare(b.label));
      },
    [attendeeList, speakerList],
  );
  const options = useMemo(
    () => ({
      champion: optionsFor("champion"),
      secondChampion: optionsFor("secondChampion"),
    }),
    [optionsFor],
  );

  const loaded =
    attendees.status !== "loading" && speakers.status !== "loading";
  // A link that matches nothing in a list that failed to load has not gone
  // anywhere, so "no longer in this event" needs both reads to have worked.
  const bothRead =
    attendees.status === "success" && speakers.status === "success";

  // The list row says who the champion is, or that the link has gone stale,
  // so a removed champion is visible without opening every track.
  const spec = useMemo<EntitySpec<Track>>(
    () => ({
      ...trackSpec,
      secondary: (track) => {
        const parts: string[] = [];
        if (track.room) parts.push(track.room);
        const { names, stale: gone } = resolveChampions(
          track,
          attendeesById,
          speakersById,
        );
        if (names.length > 0 || gone > 0) {
          if (names.length > 0)
            parts.push(
              `${names.length > 1 ? "Champions" : "Champion"}: ${names.join(" and ")}`,
            );
          // Each stale link is said, so one gone champion shows beside the
          // one who is still there. The same person named twice is neither.
          if (bothRead && gone > 0)
            parts.push(
              names.length === 0 && gone === 1
                ? "Champion no longer in this event"
                : gone === 1
                  ? "One champion no longer in this event"
                  : "Champions no longer in this event",
            );
        }
        return parts.length > 0 ? parts.join(" · ") : track.description;
      },
    }),
    [attendeesById, speakersById, bothRead],
  );

  // Stable for EntityCrudSection's post-command effect; see SpeakersSection.
  const reloadAll = useCallback(() => {
    tracks.reload();
    attendees.reload();
    speakers.reload();
  }, [tracks.reload, attendees.reload, speakers.reload]);

  const problems = [
    attendees.status === "error" ? `attendees: ${attendees.error}` : null,
    speakers.status === "error" ? `speakers: ${speakers.error}` : null,
  ].filter(Boolean);

  /** One champion picker. `other` names the picker beside it. */
  function lookup(slot: Slot, other: "first" | "second"): LookupSource {
    const otherField: Slot =
      slot === "champion" ? "secondChampion" : "champion";
    return {
      options: options[slot],
      loading: !loaded,
      error:
        problems.length > 0
          ? `The champion list could not be loaded (${problems.join("; ")}).`
          : undefined,
      empty: "No attendee or speaker matches that.",
      // The same person cannot be both champions.
      unavailable: (option, _editingId, form) => {
        const person = personOf(option.id);
        return person !== null && person === personOf(form[otherField])
          ? `Already the ${other} champion`
          : undefined;
      },
      // Only once both lists loaded: a link that matches nothing in a list
      // that failed to load has not gone anywhere.
      missing: !bothRead
        ? undefined
        : slot === "champion"
          ? "No longer in this event. Announcements show TBA for {champion} until you pick someone else."
          : "No longer in this event. Announcements leave this champion out of {champion} until you pick someone else.",
    };
  }

  return (
    <EntityCrudSection
      eventId={eventId}
      uid={uid}
      spec={spec}
      items={tracks.status === "success" ? tracks.data : []}
      loading={tracks.status === "loading"}
      error={tracks.status === "error" ? tracks.error : undefined}
      reload={reloadAll}
      lookups={{
        champion: lookup("champion", "second"),
        secondChampion: lookup("secondChampion", "first"),
      }}
    />
  );
}
